import { AuthContext } from 'contexts/AuthContext'
import { getTransformationUrl } from 'helpers/cloudinary'
import PropTypes from 'prop-types'
import React, { useContext, useRef } from 'react'
import Badge from 'react-bootstrap/Badge'
import { Fullscreen } from 'react-bootstrap-icons'
import Tooltip from 'components/Tooltip'
import styles from './Slide.module.scss'

function getWidth(orientation, aspectRatio) {
  if (aspectRatio === '32:9') {
    return orientation === 'landscape' ? 355 : 100
  } else if (aspectRatio === '9:8') {
    return orientation === 'landscape' ? 355 : 316
  } else {
    return orientation === 'landscape' ? 355 : 200
  }
}

function getHeight(orientation, aspectRatio) {
  if (aspectRatio === '32:9') {
    return orientation === 'landscape' ? 100 : 355
  } else if (aspectRatio === '9:8') {
    return orientation === 'landscape' ? 316 : 355
  } else {
    return orientation === 'landscape' ? 200 : 355
  }
}

function Slide({
  id,
  resource,
  orientation,
  aspectRatio,
  resourceOverlay,
  selected,
  onSelect,
  displayBadges,
  overlay,
  notDisplayed,
  hideWidgets,
}) {
  const { user } = useContext(AuthContext)
  const videoRef = useRef(null)
  const videoResource = resource?.resource_type === 'video'

  const handleMouseEnter = e => {
    if (videoResource) {
      videoRef.current.play()
    }
  }
  const handleMouseLeave = e => {
    if (videoResource) {
      videoRef.current.pause()
    }
  }

  if (resource && !['image', 'video', 'iframe'].includes(resource.resource_type)) {
    return null
  }

  const handleSelect = () => {
    onSelect({ ...resource, id: id })
  }

  const url = res => {
    return res
      ? getTransformationUrl(
          user.cloud_store.name,
          res.resource_type,
          res.public_id,
          getWidth(orientation, aspectRatio),
          getHeight(orientation, aspectRatio),
          videoResource || res.resource_type === 'video' ? 'mp4' : 'png'
        )
      : ''
  }

  const displayFilename = filename => {
    const maxLength = 18

    if (filename.length <= maxLength) {
      return filename
    }

    const extension = filename.substr(filename.length - 3)
    return filename.substring(0, maxLength - 6) + '...' + extension
  }

  return (
    <div
      className={`${!displayBadges && 'd-flex flex-grow-1'} ${
        selected ? styles.itemSelected : styles.itemNotSelected
      } ${notDisplayed && styles.notDisplayed}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleSelect}>
      <div style={{ position: 'relative', display: 'flex', flexGrow: '1' }}>
        {resource && resource.resource_type === 'iframe' ? (
          <iframe
            style={{ flexGrow: '1' }}
            src={resource.url}
            title='media'
            scrolling='no'
            frameBorder='0'
            className={`${styles.itemContent} ${
              selected ? styles.selected : styles.notSelected
            }`}></iframe>
        ) : (
          <>
            {videoResource ? (
              <>
                <video
                  ref={videoRef}
                  muted
                  className={`${styles.itemContent} ${
                    selected ? styles.selected : styles.notSelected
                  }`}>
                  <source
                    src={url(resource ? resource : resourceOverlay)}
                    type='video/mp4'
                  />
                </video>
              </>
            ) : (
              <img
                src={url(resource ? resource : resourceOverlay)}
                alt='media'
                className={`${styles.itemContent} ${
                  selected ? styles.selected : styles.notSelected
                }`}
              />
            )}
          </>
        )}

        {resourceOverlay && (
          <img
            src={url(resourceOverlay)}
            style={{ position: 'absolute', top: 0, left: 0 }}
            alt='media overlay'
            className={`${styles.itemContent} ${selected ? styles.selected : ''}`}></img>
        )}

        {overlay && <div className={styles.overlay}>{overlay}</div>}

        {hideWidgets && (
          <Tooltip label='This slide will be fullscreen without any widgets'>
            <div className={styles.hideWidgetsBadge}>
              <Fullscreen size={14} />
            </div>
          </Tooltip>
        )}
      </div>

      {displayBadges && (
        <React.Fragment>
          <Badge className='mr-2' pill variant='secondary'>
            {resource.resource_type}
          </Badge>
          <Badge className='mr-2' pill variant='secondary'>
            {resource.size_string}
          </Badge>
          <Badge pill variant='secondary'>
            {displayFilename(resource.filename)}
          </Badge>
        </React.Fragment>
      )}
    </div>
  )
}

Slide.propTypes = {
  resource: PropTypes.object,
  orientation: PropTypes.string,
  aspectRatio: PropTypes.string,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  displayBadges: PropTypes.bool,
  overlay: PropTypes.node,
  hideWidgets: PropTypes.bool,
}

Slide.defaultProps = {
  orientation: 'landscape',
  aspectRatio: '16:9',
  selected: false,
  onSelect: () => {},
  displayBadges: true,
  hideWidgets: false,
}

export default Slide
